import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  BarChart3,
  Copy,
  ExternalLink,
  Globe,
  LayoutTemplate,
  MousePointerSquareDashed,
  Plus,
  Save,
  Settings2,
  Sparkles,
  TextCursorInput,
  Image as ImageIcon,
  MousePointerClick,
  FormInput,
  Code2,
  Trash2,
  TestTube2,
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const BLOCK_LIBRARY = [
  { type: 'hero', label: 'Hero', description: 'Headline, copy, CTA', icon: Sparkles },
  { type: 'text', label: 'Text', description: 'Body copy block', icon: TextCursorInput },
  { type: 'bullets', label: 'Bullets', description: 'Reasons, benefits, features', icon: LayoutTemplate },
  { type: 'image', label: 'Image', description: 'Media section', icon: ImageIcon },
  { type: 'form', label: 'Form', description: 'Attach a Beluga form', icon: FormInput },
  { type: 'button', label: 'Button', description: 'CTA to another step or URL', icon: MousePointerClick },
  { type: 'custom_code', label: 'Custom code', description: 'Advanced embed or markup', icon: Code2 },
];

const clone = (value) => JSON.parse(JSON.stringify(value));
const generateId = (prefix) => `${prefix}${Math.random().toString(16).slice(2, 10)}`;

const slugifyPath = (value, fallback = 'page') => {
  const raw = (value || fallback).toLowerCase().trim();
  const slug = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const createThemeTemplate = () => ({
  accent_color: '#0f766e',
  background_color: '#f6fbfb',
  panel_color: '#ffffff',
  text_color: '#0f172a',
  muted_color: '#475569',
  heading_font: 'Manrope',
  body_font: 'Manrope',
});

const createTrackingTemplate = () => ({
  head_code: '',
  body_code: '',
  meta_pixel_id: '',
  google_tag_id: '',
});

const createBlockTemplate = (type) => {
  const base = {
    block_id: generateId('blk_'),
    type,
    enabled: true,
    settings: {},
  };

  if (type === 'hero') {
    return {
      ...base,
      settings: {
        eyebrow: 'Beluga funnel',
        headline: 'Turn traffic into booked conversations',
        body: 'Use focused copy, social proof, and a clear inline form to move visitors through the funnel.',
        cta_label: 'Get started',
        cta_url: '#beluga-primary-form',
      },
    };
  }

  if (type === 'bullets') {
    return {
      ...base,
      settings: {
        title: 'Why this works',
        items: ['Clear promise', 'Short form', 'Strong next step'],
      },
    };
  }

  if (type === 'image') {
    return {
      ...base,
      settings: {
        image_url: '',
        alt: '',
        caption: '',
      },
    };
  }

  if (type === 'form') {
    return {
      ...base,
      settings: {
        anchor_id: 'beluga-primary-form',
        form_id: '',
        title: 'Inline form',
        description: 'Choose an existing Beluga form to render here.',
      },
    };
  }

  if (type === 'button') {
    return {
      ...base,
      settings: {
        label: 'Continue',
        url: '',
        next_step_id: '',
      },
    };
  }

  if (type === 'custom_code') {
    return {
      ...base,
      settings: {
        html: '<div>Custom embed</div>',
      },
    };
  }

  return {
    ...base,
    settings: {
      body: 'Add supporting copy for this section.',
    },
  };
};

const createVariantTemplate = (name = 'Control', isControl = false) => ({
  variant_id: generateId('fvr_'),
  name,
  slug: slugifyPath(name, isControl ? 'control' : 'variation'),
  traffic_percent: isControl ? 100 : 50,
  is_control: isControl,
  status: 'active',
  seo_title: '',
  seo_description: '',
  head_code: '',
  body_code: '',
  blocks: [
    createBlockTemplate('hero'),
    createBlockTemplate('bullets'),
    createBlockTemplate('form'),
    createBlockTemplate('button'),
  ],
});

const createStepTemplate = (index) => ({
  step_id: generateId('fst_'),
  name: index === 0 ? 'Home' : index === 1 ? 'Thank you' : `Step ${index + 1}`,
  path: index === 0 ? '' : index === 1 ? 'thank-you' : slugifyPath(`step-${index + 1}`),
  position: index,
  split_test_enabled: false,
  next_step_id: null,
  variants: [createVariantTemplate('Control', true)],
});

const createDomainTemplate = (host = '', isPrimary = true) => ({
  domain_id: generateId('fdm_'),
  host,
  type: 'custom_domain',
  is_primary: isPrimary,
  dns_status: 'pending',
  ssl_status: 'pending',
});

const hydrateBlock = (block) => ({
  enabled: true,
  settings: {},
  ...block,
  settings: block.settings || {},
});

const hydrateVariant = (variant) => ({
  traffic_percent: 100,
  status: 'active',
  ...variant,
  blocks: (variant.blocks || []).map(hydrateBlock),
});

const hydrateStep = (step, index) => ({
  position: index,
  split_test_enabled: false,
  ...step,
  variants: (step.variants || []).map(hydrateVariant),
});

const hydrateFunnel = (funnel) => ({
  ...funnel,
  theme: { ...createThemeTemplate(), ...(funnel.theme || {}) },
  tracking: { ...createTrackingTemplate(), ...(funnel.tracking || {}) },
  domains: (funnel.domains || []).map((domain, index) => ({ ...createDomainTemplate('', index === 0), ...domain })),
  steps: (funnel.steps || []).map(hydrateStep),
});

const renderBlockPreview = (block, formsById) => {
  const settings = block.settings || {};

  if (block.type === 'hero') {
    return (
      <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white px-8 py-10 shadow-sm">
        <div className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">
          {settings.eyebrow || 'Beluga funnel'}
        </div>
        <div className="text-[34px] font-black tracking-tight text-slate-900">
          {settings.headline || 'Hero headline'}
        </div>
        <p className="max-w-2xl text-sm leading-7 text-slate-500">
          {settings.body || 'Add supporting copy for the top of the page.'}
        </p>
        {settings.cta_label ? (
          <div className="inline-flex rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
            {settings.cta_label}
          </div>
        ) : null}
      </div>
    );
  }

  if (block.type === 'bullets') {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-8 shadow-sm">
        <div className="text-xl font-bold text-slate-900">{settings.title || 'Highlights'}</div>
        <ul className="mt-4 grid gap-3 text-sm text-slate-500">
          {(settings.items || []).map((item, index) => (
            <li key={`${item}-${index}`} className="rounded-2xl bg-slate-50 px-4 py-3">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === 'image') {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-8 shadow-sm">
        <div className="aspect-[16/8] rounded-[24px] border border-dashed border-slate-300 bg-slate-50" />
        <div className="mt-3 text-sm text-slate-500">{settings.caption || 'Image caption'}</div>
      </div>
    );
  }

  if (block.type === 'form') {
    const linkedForm = formsById[settings.form_id];
    return (
      <div className="rounded-[28px] border border-emerald-200 bg-emerald-50/60 px-8 py-8 shadow-sm">
        <div className="text-lg font-bold text-slate-900">{settings.title || 'Inline form'}</div>
        <p className="mt-2 text-sm text-slate-600">
          {linkedForm ? `Connected to ${linkedForm.name}` : 'Choose a Beluga form to render inline on the hosted page.'}
        </p>
      </div>
    );
  }

  if (block.type === 'button') {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-8 shadow-sm">
        <div className="inline-flex rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white">
          {settings.label || 'Continue'}
        </div>
      </div>
    );
  }

  if (block.type === 'custom_code') {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-slate-950 px-6 py-6 font-mono text-xs text-emerald-200 shadow-sm">
        {settings.html || '<div>Custom code</div>'}
      </div>
    );
  }

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white px-8 py-8 shadow-sm">
      <p className="text-sm leading-7 text-slate-500">{settings.body || 'Add text here.'}</p>
    </div>
  );
};

const FunnelsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { funnelId } = useParams();
  const { user, selectedBusiness } = useAuth();

  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [funnels, setFunnels] = useState([]);
  const [forms, setForms] = useState([]);
  const [draftFunnel, setDraftFunnel] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createName, setCreateName] = useState('');

  const isAnalyticsRoute = location.pathname.endsWith('/analytics');
  const isBuilderRoute = Boolean(funnelId) && !isAnalyticsRoute;
  const selectedStep = draftFunnel?.steps?.find((step) => step.step_id === selectedStepId) || draftFunnel?.steps?.[0] || null;
  const selectedVariant = selectedStep?.variants?.find((variant) => variant.variant_id === selectedVariantId) || selectedStep?.variants?.[0] || null;
  const selectedBlock = selectedVariant?.blocks?.find((block) => block.block_id === selectedBlockId) || selectedVariant?.blocks?.[0] || null;
  const formsById = Object.fromEntries(forms.map((form) => [form.form_id, form]));

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const readJson = async (response) => {
    try {
      return await response.clone().json();
    } catch (cloneError) {
      try {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      } catch (readError) {
        return null;
      }
    }
  };

  const updateDraft = (updater) => {
    setDraftFunnel((current) => {
      const base = clone(current);
      return typeof updater === 'function' ? updater(base) : updater;
    });
  };

  const fetchFunnels = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/funnels?business_id=${businessId}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (response.ok) {
        const payload = await readJson(response);
        const data = Array.isArray(payload) ? payload.map(hydrateFunnel) : [];
        setFunnels(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    const response = await fetch(`${API_URL}/api/forms?business_id=${businessId}`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (response.ok) {
      const data = await readJson(response);
      setForms(Array.isArray(data) ? data : []);
    }
  };

  const fetchAnalytics = async (targetFunnelId) => {
    const response = await fetch(`${API_URL}/api/funnels/${targetFunnelId}/analytics`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (response.ok) {
      setAnalytics(await readJson(response));
    }
  };

  useEffect(() => {
    if (!businessId) return;
    fetchFunnels();
    fetchForms();
  }, [businessId]);

  useEffect(() => {
    if (!funnels.length) {
      setDraftFunnel(null);
      setAnalytics(null);
      return;
    }

    if (!funnelId) return;
    const matched = funnels.find((funnel) => funnel.funnel_id === funnelId);
    if (!matched) {
      navigate('/funnels', { replace: true });
      return;
    }
    const hydrated = hydrateFunnel(matched);
    setDraftFunnel(clone(hydrated));
    const firstStep = hydrated.steps?.[0];
    const firstVariant = firstStep?.variants?.[0];
    setSelectedStepId(firstStep?.step_id || null);
    setSelectedVariantId(firstVariant?.variant_id || null);
    setSelectedBlockId(firstVariant?.blocks?.[0]?.block_id || null);
    if (isAnalyticsRoute) {
      fetchAnalytics(hydrated.funnel_id);
    }
  }, [funnels, funnelId, navigate, isAnalyticsRoute]);

  const createFunnel = async () => {
    if (!createName.trim()) {
      toast.error('Provide a funnel name');
      return;
    }
    const response = await fetch(`${API_URL}/api/funnels`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ name: createName.trim() }),
    });
    const data = await readJson(response);
    if (!response.ok) {
      toast.error(data?.detail || 'Failed to create funnel');
      return;
    }
    setShowCreateDialog(false);
    setCreateName('');
    await fetchFunnels();
    if (data?.funnel_id) {
      navigate(`/funnels/${data.funnel_id}`);
    }
  };

  const saveFunnel = async () => {
    if (!draftFunnel) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/funnels/${draftFunnel.funnel_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          name: draftFunnel.name,
          status: draftFunnel.status,
          steps: draftFunnel.steps,
          theme: draftFunnel.theme,
          tracking: draftFunnel.tracking,
          domains: draftFunnel.domains,
        }),
      });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to save funnel');
      }
      const hydrated = hydrateFunnel(data);
      setDraftFunnel(clone(hydrated));
      toast.success('Funnel saved');
      await fetchFunnels();
      if (isAnalyticsRoute) {
        await fetchAnalytics(hydrated.funnel_id);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    updateDraft((next) => {
      const step = createStepTemplate(next.steps.length);
      next.steps.push(step);
      return next;
    });
  };

  const removeStep = (stepId) => {
    updateDraft((next) => {
      if (next.steps.length <= 1) return next;
      next.steps = next.steps.filter((step) => step.step_id !== stepId).map((step, index) => ({ ...step, position: index }));
      return next;
    });
  };

  const updateStepField = (stepId, key, value) => {
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === stepId);
      if (step) {
        step[key] = key === 'path' ? slugifyPath(value, step.path ? 'step' : 'home') : value;
      }
      return next;
    });
  };

  const addVariation = () => {
    if (!selectedStep) return;
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === selectedStep.step_id);
      if (!step) return next;
      step.split_test_enabled = true;
      const variation = createVariantTemplate(`Variation ${step.variants.length}`, false);
      step.variants.push(variation);
      return next;
    });
  };

  const removeVariation = (variantId) => {
    if (!selectedStep) return;
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === selectedStep.step_id);
      if (!step || step.variants.length <= 1) return next;
      step.variants = step.variants.filter((variant) => variant.variant_id !== variantId);
      if (step.variants.length <= 1) {
        step.split_test_enabled = false;
        step.variants[0].traffic_percent = 100;
      }
      return next;
    });
  };

  const updateVariantField = (variantId, key, value) => {
    updateDraft((next) => {
      for (const step of next.steps) {
        const variant = step.variants.find((entry) => entry.variant_id === variantId);
        if (!variant) continue;
        variant[key] = key === 'slug' ? slugifyPath(value, variant.slug || 'variant') : value;
      }
      return next;
    });
  };

  const toggleSplitTest = (checked) => {
    if (!selectedStep) return;
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === selectedStep.step_id);
      if (!step) return next;
      step.split_test_enabled = checked && step.variants.length > 1;
      if (!checked) {
        step.variants = step.variants.map((variant, index) => ({
          ...variant,
          traffic_percent: index === 0 ? 100 : 0,
        }));
      }
      return next;
    });
  };

  const addBlock = (type) => {
    if (!selectedVariant) return;
    updateDraft((next) => {
      for (const step of next.steps) {
        const variant = step.variants.find((entry) => entry.variant_id === selectedVariant.variant_id);
        if (!variant) continue;
        const block = createBlockTemplate(type);
        variant.blocks.push(block);
        setSelectedBlockId(block.block_id);
      }
      return next;
    });
  };

  const removeBlock = (blockId) => {
    if (!selectedVariant) return;
    updateDraft((next) => {
      for (const step of next.steps) {
        const variant = step.variants.find((entry) => entry.variant_id === selectedVariant.variant_id);
        if (!variant) continue;
        variant.blocks = variant.blocks.filter((block) => block.block_id !== blockId);
      }
      return next;
    });
    setSelectedBlockId(null);
  };

  const updateBlockSetting = (blockId, key, value) => {
    updateDraft((next) => {
      for (const step of next.steps) {
        for (const variant of step.variants) {
          const block = variant.blocks.find((entry) => entry.block_id === blockId);
          if (!block) continue;
          block.settings[key] = value;
        }
      }
      return next;
    });
  };

  const updateDomainField = (domainId, key, value) => {
    updateDraft((next) => {
      next.domains = next.domains.map((domain) => {
        if (domain.domain_id !== domainId) return domain;
        return {
          ...domain,
          [key]: key === 'host' ? value.trim().toLowerCase() : value,
        };
      });
      return next;
    });
  };

  const addDomain = () => {
    updateDraft((next) => {
      next.domains.push(createDomainTemplate('', next.domains.length === 0));
      return next;
    });
  };

  const removeDomain = (domainId) => {
    updateDraft((next) => {
      next.domains = next.domains.filter((domain) => domain.domain_id !== domainId);
      if (next.domains.length && !next.domains.some((domain) => domain.is_primary)) {
        next.domains[0].is_primary = true;
      }
      return next;
    });
  };

  const setPrimaryDomain = (domainId) => {
    updateDraft((next) => {
      next.domains = next.domains.map((domain) => ({
        ...domain,
        is_primary: domain.domain_id === domainId,
      }));
      return next;
    });
  };

  const copyToClipboard = async (value, label) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const renderLibrary = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" />
            Hosted Funnels
          </div>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900">Build full pages, not just forms.</h1>
          <p className="mt-2 max-w-3xl text-slate-500">
            Create hosted funnel pages with steps, inline forms, tracking code, split-test variations, and publishable URLs.
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New funnel
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading funnels...</div>
      ) : funnels.length === 0 ? (
        <Alert>
          <AlertDescription>Create your first funnel to publish a hosted landing page with step URLs and inline forms.</AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {funnels.map((funnel) => (
            <Card key={funnel.funnel_id} className="border-slate-200">
              <CardContent className="space-y-5 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-lg font-bold text-slate-900">{funnel.name}</div>
                    <div className="mt-1 text-sm text-slate-500">{funnel.total_steps} steps</div>
                  </div>
                  <Badge variant="secondary" className="capitalize">{funnel.status}</Badge>
                </div>

                <div className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Primary URL</div>
                  <div className="mt-2 break-all">{funnel.public_url || funnel.preview_url}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl border border-slate-200 px-4 py-3">
                    <div className="text-slate-500">Views</div>
                    <div className="mt-1 text-xl font-bold text-slate-900">{funnel.view_count}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 px-4 py-3">
                    <div className="text-slate-500">Conversions</div>
                    <div className="mt-1 text-xl font-bold text-slate-900">{funnel.conversion_count}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => navigate(`/funnels/${funnel.funnel_id}`)}>Open builder</Button>
                  <Button variant="outline" onClick={() => navigate(`/funnels/${funnel.funnel_id}/analytics`)}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    Analytics
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Builder tab state
  const [builderTab, setBuilderTab] = useState('steps'); // 'steps' | 'settings' | 'analytics'
  const [stepTab, setStepTab] = useState('overview'); // 'overview' | 'blocks' | 'step-settings'

  const renderBlockInspector = () => {
    if (!selectedBlock) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Selected block</div>
            <div className="mt-0.5 font-semibold capitalize text-slate-900">{selectedBlock.type.replace('_', ' ')}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => removeBlock(selectedBlock.block_id)}>
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
        {selectedBlock.type === 'hero' && (
          <>
            <div className="space-y-1.5"><Label className="text-xs">Eyebrow</Label><Input value={selectedBlock.settings.eyebrow || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'eyebrow', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Headline</Label><Input value={selectedBlock.settings.headline || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'headline', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Body text</Label><Textarea value={selectedBlock.settings.body || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'body', e.target.value)} className="min-h-[80px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">CTA label</Label><Input value={selectedBlock.settings.cta_label || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'cta_label', e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">CTA URL</Label><Input value={selectedBlock.settings.cta_url || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'cta_url', e.target.value)} /></div>
            </div>
          </>
        )}
        {selectedBlock.type === 'text' && <div className="space-y-1.5"><Label className="text-xs">Body</Label><Textarea value={selectedBlock.settings.body || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'body', e.target.value)} className="min-h-[100px]" /></div>}
        {selectedBlock.type === 'bullets' && (
          <>
            <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={selectedBlock.settings.title || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'title', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Items (one per line)</Label><Textarea value={(selectedBlock.settings.items || []).join('\n')} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'items', e.target.value.split('\n').map((i) => i.trim()).filter(Boolean))} className="min-h-[100px]" /></div>
          </>
        )}
        {selectedBlock.type === 'image' && (
          <>
            <div className="space-y-1.5"><Label className="text-xs">Image URL</Label><Input value={selectedBlock.settings.image_url || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'image_url', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Alt text</Label><Input value={selectedBlock.settings.alt || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'alt', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Caption</Label><Input value={selectedBlock.settings.caption || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'caption', e.target.value)} /></div>
          </>
        )}
        {selectedBlock.type === 'form' && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Linked form</Label>
              <Select value={selectedBlock.settings.form_id || 'none'} onValueChange={(v) => updateBlockSetting(selectedBlock.block_id, 'form_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Choose form" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No form</SelectItem>{forms.map((f) => <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Section title</Label><Input value={selectedBlock.settings.title || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'title', e.target.value)} /></div>
          </>
        )}
        {selectedBlock.type === 'button' && (
          <>
            <div className="space-y-1.5"><Label className="text-xs">Label</Label><Input value={selectedBlock.settings.label || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'label', e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Step target</Label>
              <Select value={selectedBlock.settings.next_step_id || 'none'} onValueChange={(v) => updateBlockSetting(selectedBlock.block_id, 'next_step_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">No step target</SelectItem>{draftFunnel.steps.filter((s) => s.step_id !== selectedStep?.step_id).map((s) => <SelectItem key={s.step_id} value={s.step_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Override URL</Label><Input value={selectedBlock.settings.url || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'url', e.target.value)} placeholder="https://example.com" /></div>
          </>
        )}
        {selectedBlock.type === 'custom_code' && <div className="space-y-1.5"><Label className="text-xs">HTML / embed</Label><Textarea value={selectedBlock.settings.html || ''} onChange={(e) => updateBlockSetting(selectedBlock.block_id, 'html', e.target.value)} className="min-h-[180px] font-mono text-xs" /></div>}
      </div>
    );
  };

  const renderBuilder = () => {
    if (!draftFunnel) return null;

    const hasDomain = draftFunnel.domains?.some((d) => d.host);
    const primaryDomain = draftFunnel.domains?.find((d) => d.is_primary);

    return (
      <div className="flex h-full flex-col">
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/funnels')}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="h-4 w-px bg-slate-200" />
            <h1 className="text-base font-bold text-slate-900">{draftFunnel.name}</h1>
            <Badge variant={draftFunnel.status === 'published' ? 'default' : 'secondary'} className="capitalize text-xs">
              {draftFunnel.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(draftFunnel.preview_url, '_blank')}>
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Preview
            </Button>
            <Button size="sm" onClick={saveFunnel} disabled={saving}>
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex gap-1 border-b border-slate-200 bg-white px-6">
          {[
            { id: 'steps', label: 'Steps' },
            { id: 'settings', label: 'Settings' },
            { id: 'analytics', label: 'Analytics' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setBuilderTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                builderTab === tab.id
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Steps tab ── */}
        {builderTab === 'steps' && (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: steps list */}
            <div className="w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-white">
              <div className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Funnel Steps</span>
                </div>
                <div className="space-y-1">
                  {draftFunnel.steps.map((step) => (
                    <button
                      key={step.step_id}
                      type="button"
                      onClick={() => {
                        setSelectedStepId(step.step_id);
                        setSelectedVariantId(step.variants?.[0]?.variant_id || null);
                        setSelectedBlockId(step.variants?.[0]?.blocks?.[0]?.block_id || null);
                        setStepTab('overview');
                      }}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                        step.step_id === selectedStep?.step_id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                        step.step_id === selectedStep?.step_id ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {draftFunnel.steps.indexOf(step) + 1}
                      </div>
                      <span className="truncate text-sm font-medium">{step.name}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addStep}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-500 transition hover:border-slate-400 hover:text-slate-700"
                >
                  <Plus className="h-4 w-4" />
                  Add New Step
                </button>
              </div>
            </div>

            {/* Center: step detail */}
            <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
              {!selectedStep ? (
                <div className="flex h-full items-center justify-center text-slate-400">Select a step to view it</div>
              ) : (
                <div className="mx-auto max-w-3xl space-y-5">
                  {/* Step header */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900">{selectedStep.name}</h2>
                    <div className="flex items-center gap-2">
                      {draftFunnel.steps.length > 1 && (
                        <Button variant="outline" size="sm" onClick={() => removeStep(selectedStep.step_id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                          Delete Step
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Domain warning banner */}
                  {!hasDomain && (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      <Globe className="h-4 w-4 shrink-0" />
                      <span>Add a domain in <button type="button" onClick={() => setBuilderTab('settings')} className="font-semibold underline">Settings</button> to see your funnel live.</span>
                    </div>
                  )}

                  {/* Step sub-tabs */}
                  <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
                    {[
                      { id: 'overview', label: 'Overview' },
                      { id: 'blocks', label: 'Page Builder' },
                      { id: 'step-settings', label: 'Step Settings' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setStepTab(tab.id)}
                        className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${
                          stepTab === tab.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Overview sub-tab: GHL-style variant cards */}
                  {stepTab === 'overview' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        {selectedStep.variants.map((variant, idx) => (
                          <div
                            key={variant.variant_id}
                            onClick={() => { setSelectedVariantId(variant.variant_id); setSelectedBlockId(variant.blocks?.[0]?.block_id || null); }}
                            className={`group cursor-pointer rounded-xl border-2 bg-white transition ${
                              variant.variant_id === selectedVariant?.variant_id
                                ? 'border-blue-500 shadow-md'
                                : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                            }`}
                          >
                            {/* Page thumbnail mockup */}
                            <div className="relative h-44 overflow-hidden rounded-t-xl bg-gradient-to-br from-slate-100 to-slate-200">
                              <div className="absolute inset-0 flex flex-col gap-1.5 p-3 opacity-60">
                                <div className="h-4 w-3/4 rounded bg-slate-400" />
                                <div className="h-3 w-1/2 rounded bg-slate-300" />
                                <div className="mt-1 h-8 w-2/3 rounded bg-blue-400" />
                                <div className="mt-2 h-2 w-full rounded bg-slate-300" />
                                <div className="h-2 w-5/6 rounded bg-slate-300" />
                                <div className="h-2 w-4/6 rounded bg-slate-300" />
                              </div>
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                                <div className="rounded-lg bg-white/90 px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm">
                                  Click to select
                                </div>
                              </div>
                            </div>
                            {/* Variant info */}
                            <div className="p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                    idx === 0 ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    {idx === 0 ? 'Control' : 'Variation'}
                                  </span>
                                  <span className="text-sm font-medium text-slate-700">{variant.name}</span>
                                </div>
                                {selectedStep.split_test_enabled && (
                                  <span className="text-xs text-slate-400">{variant.traffic_percent}%</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {/* Add variation card */}
                        <button
                          type="button"
                          onClick={addVariation}
                          className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-white text-slate-400 transition hover:border-blue-300 hover:text-blue-500"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                            <Plus className="h-5 w-5" />
                          </div>
                          <div className="text-center">
                            <div className="text-sm font-semibold">Create Variation</div>
                            <div className="text-xs">Run a split test on this step</div>
                          </div>
                        </button>
                      </div>

                      {/* Split test toggle */}
                      {selectedStep.variants.length > 1 && (
                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">Split Test Running</div>
                            <div className="text-xs text-slate-500">Traffic is split between variants</div>
                          </div>
                          <Switch checked={selectedStep.split_test_enabled} onCheckedChange={toggleSplitTest} />
                        </div>
                      )}

                      {/* Edit page button */}
                      {selectedVariant && (
                        <Button className="w-full" onClick={() => setStepTab('blocks')}>
                          <MousePointerSquareDashed className="mr-2 h-4 w-4" />
                          Edit Page Blocks
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Page Builder sub-tab */}
                  {stepTab === 'blocks' && selectedVariant && (
                    <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
                      {/* Block canvas */}
                      <div className="space-y-4">
                        {/* Block library */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Add Block</div>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                            {BLOCK_LIBRARY.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button key={item.type} type="button" onClick={() => addBlock(item.type)}
                                  className="flex flex-col items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-3 text-center text-xs transition hover:border-blue-300 hover:bg-blue-50">
                                  <Icon className="h-4 w-4 text-slate-500" />
                                  <span className="font-medium text-slate-700">{item.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {/* Blocks */}
                        <div className="rounded-xl border border-slate-200 bg-white" style={{ background: `linear-gradient(180deg, ${draftFunnel.theme.background_color} 0%, #f8f9fa 100%)` }}>
                          {selectedVariant.blocks.length === 0 ? (
                            <div className="flex h-40 items-center justify-center text-sm text-slate-400">No blocks yet — add one above</div>
                          ) : (
                            <div className="space-y-3 p-4">
                              {selectedVariant.blocks.map((block) => (
                                <div
                                  key={block.block_id}
                                  onClick={() => setSelectedBlockId(block.block_id)}
                                  className={`relative cursor-pointer rounded-xl border-2 transition ${
                                    selectedBlock?.block_id === block.block_id
                                      ? 'border-blue-500 shadow-sm'
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                >
                                  <div className="absolute right-2 top-2 z-10 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold capitalize text-slate-500 shadow-sm">
                                    {block.type.replace('_', ' ')}
                                  </div>
                                  <div className="overflow-hidden rounded-[10px]">
                                    {renderBlockPreview(block, formsById)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Block inspector */}
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        {selectedBlock
                          ? renderBlockInspector()
                          : <div className="text-center text-sm text-slate-400 py-8">Click a block to edit it</div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Step Settings sub-tab */}
                  {stepTab === 'step-settings' && (
                    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step Name</Label>
                        <Input value={selectedStep.name} onChange={(e) => updateStepField(selectedStep.step_id, 'name', e.target.value)} placeholder="Home" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step URL Path</Label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-400">/</span>
                          <Input value={selectedStep.path || ''} onChange={(e) => updateStepField(selectedStep.step_id, 'path', e.target.value)} placeholder="home" />
                        </div>
                        <p className="text-xs text-slate-400">Nested paths are supported</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next Step</Label>
                        <Select value={selectedStep.next_step_id || 'none'} onValueChange={(v) => updateStepField(selectedStep.step_id, 'next_step_id', v === 'none' ? null : v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No next step</SelectItem>
                            {draftFunnel.steps.filter((s) => s.step_id !== selectedStep.step_id).map((s) => <SelectItem key={s.step_id} value={s.step_id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedVariant && (
                        <>
                          <div className="border-t border-slate-100 pt-4 space-y-1.5">
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">SEO Title</Label>
                            <Input value={selectedVariant.seo_title || ''} onChange={(e) => updateVariantField(selectedVariant.variant_id, 'seo_title', e.target.value)} />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">SEO Description</Label>
                            <Textarea value={selectedVariant.seo_description || ''} onChange={(e) => updateVariantField(selectedVariant.variant_id, 'seo_description', e.target.value)} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Settings tab ── */}
        {builderTab === 'settings' && (
          <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
            <div className="mx-auto max-w-2xl space-y-6">
              <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
                <h3 className="font-bold text-slate-900">General</h3>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Funnel Name</Label>
                  <Input value={draftFunnel.name} onChange={(e) => updateDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</Label>
                  <Select value={draftFunnel.status} onValueChange={(v) => updateDraft((d) => ({ ...d, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">Domain</h3>
                  <Button variant="outline" size="sm" onClick={addDomain}><Plus className="mr-1.5 h-3.5 w-3.5" />Add Domain</Button>
                </div>
                {draftFunnel.domains.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
                    <Globe className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-sm font-medium text-slate-500">No domain connected</p>
                    <p className="text-xs text-slate-400">Add a domain to publish your funnel live</p>
                    <Button size="sm" className="mt-3" onClick={addDomain}>Connect Domain</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {draftFunnel.domains.map((domain) => (
                      <div key={domain.domain_id} className="rounded-xl border border-slate-200 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            {domain.is_primary && <Badge className="text-[10px]">Primary</Badge>}
                            <Badge variant="secondary" className="text-[10px]">{domain.type}</Badge>
                          </div>
                          <div className="flex gap-2">
                            {!domain.is_primary && <Button variant="outline" size="sm" onClick={() => setPrimaryDomain(domain.domain_id)} className="text-xs">Set Primary</Button>}
                            <Button variant="ghost" size="icon" onClick={() => removeDomain(domain.domain_id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                          </div>
                        </div>
                        <Input value={domain.host} onChange={(e) => updateDomainField(domain.domain_id, 'host', e.target.value)} placeholder="offers.yourdomain.com" />
                        <div className="flex gap-3 text-xs text-slate-500">
                          <span className={`rounded-full px-2 py-0.5 ${domain.dns_status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100'}`}>DNS: {domain.dns_status}</span>
                          <span className={`rounded-full px-2 py-0.5 ${domain.ssl_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100'}`}>SSL: {domain.ssl_status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preview URL</Label>
                  <div className="flex gap-2">
                    <Input value={draftFunnel.preview_url || ''} readOnly className="bg-slate-50 text-slate-500 text-xs" />
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(draftFunnel.preview_url, 'URL')}><Copy className="h-4 w-4" /></Button>
                    <Button variant="outline" size="icon" onClick={() => window.open(draftFunnel.preview_url, '_blank')}><ExternalLink className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
                <h3 className="font-bold text-slate-900">Tracking</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Meta Pixel ID</Label>
                    <Input value={draftFunnel.tracking.meta_pixel_id} onChange={(e) => updateDraft((d) => ({ ...d, tracking: { ...d.tracking, meta_pixel_id: e.target.value } }))} placeholder="1234567890" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Google Tag ID</Label>
                    <Input value={draftFunnel.tracking.google_tag_id} onChange={(e) => updateDraft((d) => ({ ...d, tracking: { ...d.tracking, google_tag_id: e.target.value } }))} placeholder="G-XXXXXXXXXX" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Head Code</Label>
                  <Textarea value={draftFunnel.tracking.head_code} onChange={(e) => updateDraft((d) => ({ ...d, tracking: { ...d.tracking, head_code: e.target.value } }))} className="min-h-[100px] font-mono text-xs" placeholder="<!-- paste scripts that go in <head> -->" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Body Code</Label>
                  <Textarea value={draftFunnel.tracking.body_code} onChange={(e) => updateDraft((d) => ({ ...d, tracking: { ...d.tracking, body_code: e.target.value } }))} className="min-h-[100px] font-mono text-xs" placeholder="<!-- paste scripts that go before </body> -->" />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveFunnel} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving…' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Analytics tab ── */}
        {builderTab === 'analytics' && (
          <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
            {renderAnalytics ? (
              <div className="mx-auto max-w-4xl">
                {/* Inline analytics */}
                {!analytics ? (
                  <div className="text-center py-16 text-slate-400">No analytics yet for this funnel.</div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-3">
                      {[
                        { label: 'Total Views', value: analytics.views },
                        { label: 'CTA Clicks', value: analytics.cta_clicks },
                        { label: 'Form Submits', value: analytics.form_submits },
                      ].map((m) => (
                        <Card key={m.label} className="border-slate-200">
                          <CardContent className="p-5">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{m.label}</div>
                            <div className="mt-1 text-3xl font-black text-slate-900">{m.value}</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    <Card className="border-slate-200">
                      <CardHeader><CardTitle className="text-base">Step Breakdown</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        {(analytics.step_stats || []).map((step) => (
                          <div key={step.step_id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
                            <span className="font-medium text-slate-700">{step.step_name}</span>
                            <Badge variant="secondary">{step.views} views</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const renderAnalytics = () => {
    if (!draftFunnel) return null;
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <button type="button" onClick={() => navigate('/funnels')} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" />
              Back to funnels
            </button>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900">{draftFunnel.name}</h1>
              <p className="mt-2 max-w-3xl text-slate-500">
                Hosted funnel performance across step URLs, button clicks, and inline form conversions.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(`/funnels/${draftFunnel.funnel_id}`)}>
              Open builder
            </Button>
            <Button variant="outline" onClick={() => window.open(draftFunnel.public_url || draftFunnel.preview_url, '_blank')}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open public page
            </Button>
          </div>
        </div>

        {!analytics ? (
          <div className="text-sm text-slate-500">Loading analytics...</div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              {[
                { label: 'Views', value: analytics.views },
                { label: 'CTA clicks', value: analytics.cta_clicks },
                { label: 'Form submits', value: analytics.form_submits },
                { label: 'Top paths', value: Object.keys(analytics.top_paths || {}).length },
                { label: 'Top domains', value: Object.keys(analytics.top_domains || {}).length },
              ].map((metric) => (
                <Card key={metric.label} className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="text-sm text-slate-500">{metric.label}</div>
                    <div className="mt-2 text-3xl font-black tracking-tight text-slate-900">{metric.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Step views</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {analytics.step_stats.map((step) => (
                    <div key={step.step_id} className="rounded-3xl border border-slate-200 p-5">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">{step.path || '/'}</div>
                          <div className="mt-1 text-lg font-semibold text-slate-900">{step.step_name}</div>
                        </div>
                        <Badge variant="secondary">{step.views} views</Badge>
                      </div>
                      <div className="mt-4 space-y-2 text-sm">
                        {Object.entries(step.variant_breakdown || {}).map(([variant, count]) => (
                          <div key={variant} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-3">
                            <span className="text-slate-600">{variant}</span>
                            <span className="font-semibold text-slate-900">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Top sources</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="mb-3 text-sm font-semibold text-slate-900">Paths</div>
                    <div className="space-y-2">
                      {Object.entries(analytics.top_paths || {}).map(([path, count]) => (
                        <div key={path} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-3 text-sm">
                          <span className="text-slate-600">{path}</span>
                          <span className="font-semibold text-slate-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-3 text-sm font-semibold text-slate-900">Domains</div>
                    <div className="space-y-2">
                      {Object.entries(analytics.top_domains || {}).map(([domain, count]) => (
                        <div key={domain} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-3 text-sm">
                          <span className="text-slate-600">{domain}</span>
                          <span className="font-semibold text-slate-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    );
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return <div className="text-slate-500">Admin access required.</div>;
  }

  if (!businessId) {
    return (
      <Alert>
        <AlertDescription>Select a business before creating or editing funnels.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {isAnalyticsRoute ? renderAnalytics() : isBuilderRoute ? renderBuilder() : renderLibrary()}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Create hosted funnel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Funnel name</Label>
              <Input value={createName} onChange={(event) => setCreateName(event.target.value)} placeholder="Buyer landing page" />
            </div>
            <Alert>
              <AlertDescription>
                New funnels start with a Home step and a Thank you step. Add domains, connect forms, and publish when ready.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createFunnel}>Create funnel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FunnelsPage;
